import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Loader2, MapPin, CalendarDays, User, ClipboardCheck } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { MEDIA } from "@/lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { id: "property", label: "Property Inspection", desc: "Full residential / buyer's inspection" },
  { id: "predrywall", label: "Pre-Drywall Inspection", desc: "Framing, rough-ins & structure" },
  { id: "newconstruction", label: "New Construction", desc: "Builder walk-through prep" },
  { id: "commercial", label: "Commercial Inspection", desc: "Commercial-grade systems & ADA" },
  { id: "pool", label: "Pool Inspection", desc: "Vessel, pumps, heaters & safety" },
  { id: "drone", label: "Drone / Roof Inspection", desc: "FAA-certified aerial roof capture" },
  { id: "radon", label: "Radon Testing", desc: "EPA-standard radon gas measurement" },
  { id: "sewer", label: "Sewer Scope", desc: "Camera scope of the main sewer line" },
  { id: "stucco", label: "Stucco Inspection", desc: "Moisture & EIFS stucco evaluation" },
  { id: "custom", label: "Custom Inspection", desc: "Tailored to your specific concern" },
];

const TIME_SLOTS = ["8:00 AM", "9:30 AM", "11:00 AM", "12:30 PM", "2:00 PM", "3:30 PM", "5:00 PM"];

const STEPS = [
  { label: "What & Where", icon: MapPin },
  { label: "When", icon: CalendarDays },
  { label: "Who to Contact", icon: User },
  { label: "Review & Schedule", icon: ClipboardCheck },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const emptyForm = {
  services: [],
  address: "", unit: "", city: "", state: "GA", zip_code: "",
  under_contract: "", inspection_reason: "", due_diligence_end: "", multi_unit: "",
  preferred_date: "", preferred_time: "",
  is_company: false, first_name: "", last_name: "", email: "", phone: "",
  notes: "", sms_consent: false, agent_name: "", agent_email: "",
};

export default function Schedule() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [calMonth, setCalMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (k) => (e) => set(k, e.target.value);

  const toggleService = (id) => {
    const label = SERVICE_OPTIONS.find((s) => s.id === id).label;
    setForm((f) => ({
      ...f,
      services: f.services.includes(label) ? f.services.filter((x) => x !== label) : [...f.services, label],
    }));
  };

  const validateStep = () => {
    if (step === 0) {
      if (form.services.length === 0) return toast.error("Select at least one inspection service."), false;
      if (!form.address || !form.city) return toast.error("Please enter the property address & city."), false;
    }
    if (step === 1) {
      if (!form.preferred_date || !form.preferred_time) return toast.error("Choose a date and time."), false;
    }
    if (step === 2) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone)
        return toast.error("Please complete your contact details."), false;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 3)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/inspections`, form);
      setDone(true);
      toast.success("Inspection request submitted — we'll confirm shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // calendar helpers
  const y = calMonth.getFullYear();
  const m = calMonth.getMonth();
  const firstDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const pickDay = (d) => {
    const date = new Date(y, m, d);
    if (date < today) return;
    set("preferred_date", `${MONTHS[m]} ${d}, ${y}`);
  };

  const field = "w-full bg-transparent border-0 border-b border-white/20 focus:border-gold outline-none py-3 text-cream placeholder:text-cream/30 transition-colors duration-300";
  const label = "font-display uppercase tracking-[0.18em] text-[11px] text-gold/80 mb-1 block";
  const selectCls = `${field} appearance-none cursor-pointer`;

  return (
    <div className="bg-ink min-h-screen" data-testid="schedule-page">
      <Navbar />

      <section className="relative pt-36 pb-14 md:pt-44 overflow-hidden">
        <div className="absolute inset-0">
          <img src={MEDIA.extAlt} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <span className="font-serif italic text-gold tracking-[0.25em] text-sm">Book An Inspection</span>
            <h1 className="font-display uppercase text-cream leading-[0.9] tracking-tight text-5xl md:text-7xl mt-3">
              Schedule a Home or <br /> <span className="text-gold">Commercial</span> Inspection
            </h1>
            <p className="text-cream/60 mt-5 max-w-xl leading-relaxed">
              Real-time availability across Cumming, GA & surrounding metro Atlanta. Book in four quick steps.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative bg-ink pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="border border-gold/40 bg-surface p-12 text-center max-w-2xl mx-auto" data-testid="schedule-success">
              <div className="mx-auto w-16 h-16 rounded-full border border-gold flex items-center justify-center mb-6">
                <Check className="text-gold" size={30} />
              </div>
              <h3 className="font-display uppercase text-cream text-3xl tracking-tight">Request Received</h3>
              <p className="text-cream/60 mt-3 leading-relaxed">
                Thank you, {form.first_name}. We've received your request for{" "}
                <span className="text-gold">{form.services.join(", ")}</span> at {form.address}, {form.city}.
                Our team will confirm your {form.preferred_date} · {form.preferred_time} appointment shortly.
              </p>
            </motion.div>
          ) : (
            <>
              {/* Stepper */}
              <div className="grid grid-cols-4 gap-2 md:gap-4 mb-12" data-testid="step-indicator">
                {STEPS.map((s, i) => {
                  const Icon = s.icon;
                  const active = i === step;
                  const complete = i < step;
                  return (
                    <button key={s.label} onClick={() => i < step && setStep(i)} className="text-left group">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-9 h-9 flex items-center justify-center border transition-colors duration-300 ${active ? "bg-gold border-gold text-ink" : complete ? "border-gold text-gold" : "border-white/20 text-cream/40"}`}>
                          {complete ? <Check size={16} /> : <Icon size={16} />}
                        </div>
                        <span className={`hidden md:block font-display uppercase tracking-[0.12em] text-xs ${active ? "text-cream" : "text-cream/40"}`}>{s.label}</span>
                      </div>
                      <div className={`h-[2px] w-full transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-white/10"}`} />
                    </button>
                  );
                })}
              </div>

              <div className="border border-white/10 bg-surface p-6 md:p-12 min-h-[440px]">
                <AnimatePresence mode="wait">
                  {/* STEP 1 — What & Where */}
                  {step === 0 && (
                    <motion.div key="s0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }}>
                      <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-6">Services</h2>
                      <div className="grid sm:grid-cols-2 gap-3 mb-10">
                        {SERVICE_OPTIONS.map((s) => {
                          const checked = form.services.includes(s.label);
                          return (
                            <button key={s.id} onClick={() => toggleService(s.id)} data-testid={`service-option-${s.id}`}
                              className={`text-left border p-4 flex items-start gap-3 transition-all duration-300 ${checked ? "border-gold bg-gold/5" : "border-white/15 hover:border-white/40"}`}>
                              <span className={`mt-0.5 w-5 h-5 flex-shrink-0 border flex items-center justify-center ${checked ? "bg-gold border-gold text-ink" : "border-white/40"}`}>
                                {checked && <Check size={13} />}
                              </span>
                              <span>
                                <span className="block font-display uppercase tracking-wide text-cream text-sm">{s.label}</span>
                                <span className="block text-cream/50 text-xs mt-0.5">{s.desc}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-6">Location</h2>
                      <div className="grid md:grid-cols-2 gap-6 mb-8">
                        <div className="md:col-span-2"><label className={label}>Street Address *</label><input className={field} value={form.address} onChange={onChange("address")} placeholder="1200 Estate Ridge Dr" data-testid="addr-street" /></div>
                        <div><label className={label}>Unit / Apt</label><input className={field} value={form.unit} onChange={onChange("unit")} placeholder="Optional" data-testid="addr-unit" /></div>
                        <div><label className={label}>City *</label><input className={field} value={form.city} onChange={onChange("city")} placeholder="Cumming" data-testid="addr-city" /></div>
                        <div><label className={label}>State</label><input className={field} value={form.state} onChange={onChange("state")} data-testid="addr-state" /></div>
                        <div><label className={label}>Zip Code</label><input className={field} value={form.zip_code} onChange={onChange("zip_code")} placeholder="30040" data-testid="addr-zip" /></div>
                      </div>

                      <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-6">Additional Info</h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className={label}>Is the property currently under contract? *</label>
                          <select className={selectCls} value={form.under_contract} onChange={onChange("under_contract")} data-testid="info-contract">
                            <option value="" className="bg-surface">Select…</option>
                            <option value="Yes" className="bg-surface">Yes</option>
                            <option value="No" className="bg-surface">No</option>
                          </select>
                        </div>
                        <div>
                          <label className={label}>If not, pre-listing or maintenance?</label>
                          <select className={selectCls} value={form.inspection_reason} onChange={onChange("inspection_reason")} data-testid="info-reason">
                            <option value="" className="bg-surface">Select…</option>
                            <option value="Pre-Listing" className="bg-surface">Pre-Listing</option>
                            <option value="Maintenance" className="bg-surface">Maintenance</option>
                            <option value="N/A" className="bg-surface">N/A</option>
                          </select>
                        </div>
                        <div>
                          <label className={label}>When does your due diligence end?</label>
                          <input type="date" className={field} value={form.due_diligence_end} onChange={onChange("due_diligence_end")} data-testid="info-duediligence" />
                        </div>
                        <div>
                          <label className={label}>Is this a multi-unit dwelling?</label>
                          <select className={selectCls} value={form.multi_unit} onChange={onChange("multi_unit")} data-testid="info-multiunit">
                            <option value="" className="bg-surface">Select…</option>
                            <option value="Yes" className="bg-surface">Yes</option>
                            <option value="No" className="bg-surface">No</option>
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 2 — When */}
                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }} className="grid md:grid-cols-2 gap-10">
                      <div>
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="font-display uppercase text-cream text-2xl tracking-tight">{MONTHS[m]} {y}</h2>
                          <div className="flex gap-2">
                            <button onClick={() => setCalMonth(new Date(y, m - 1, 1))} className="w-9 h-9 border border-white/20 hover:border-gold hover:text-gold text-cream flex items-center justify-center" data-testid="cal-prev"><ChevronLeft size={18} /></button>
                            <button onClick={() => setCalMonth(new Date(y, m + 1, 1))} className="w-9 h-9 border border-white/20 hover:border-gold hover:text-gold text-cream flex items-center justify-center" data-testid="cal-next"><ChevronRight size={18} /></button>
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {DOW.map((d) => <div key={d} className="text-center text-cream/40 text-xs font-display tracking-wider py-2">{d}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {cells.map((d, i) => {
                            if (!d) return <div key={i} />;
                            const date = new Date(y, m, d);
                            const past = date < today;
                            const selected = form.preferred_date === `${MONTHS[m]} ${d}, ${y}`;
                            return (
                              <button key={i} disabled={past} onClick={() => pickDay(d)} data-testid={`cal-day-${d}`}
                                className={`aspect-square flex items-center justify-center text-sm transition-all duration-200 ${selected ? "bg-gold text-ink" : past ? "text-cream/20 cursor-not-allowed" : "text-cream hover:bg-white/10 border border-transparent hover:border-gold/40"}`}>
                                {d}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-6">Available Times</h2>
                        {!form.preferred_date && <p className="text-cream/40 text-sm mb-4">Select a date to view time slots.</p>}
                        <div className="grid grid-cols-2 gap-3">
                          {TIME_SLOTS.map((t, i) => (
                            <button key={t} disabled={!form.preferred_date} onClick={() => set("preferred_time", t)} data-testid={`time-slot-${i}`}
                              className={`border py-3 font-display tracking-wide text-sm transition-all duration-200 ${form.preferred_time === t ? "bg-gold border-gold text-ink" : "border-white/20 text-cream hover:border-gold disabled:opacity-30 disabled:hover:border-white/20"}`}>
                              {t}
                            </button>
                          ))}
                        </div>
                        {form.preferred_date && (
                          <p className="text-cream/60 mt-6 text-sm">Selected: <span className="text-gold">{form.preferred_date}{form.preferred_time ? ` · ${form.preferred_time}` : ""}</span></p>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 3 — Who to Contact */}
                  {step === 2 && (
                    <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }}>
                      <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-6">Client</h2>
                      <label className="flex items-center gap-3 mb-8 cursor-pointer">
                        <span className={`w-5 h-5 border flex items-center justify-center ${form.is_company ? "bg-gold border-gold text-ink" : "border-white/40"}`} onClick={() => set("is_company", !form.is_company)}>
                          {form.is_company && <Check size={13} />}
                        </span>
                        <span className="text-cream/70 text-sm" onClick={() => set("is_company", !form.is_company)}>Client is a Company / Organization</span>
                      </label>
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div><label className={label}>First Name *</label><input className={field} value={form.first_name} onChange={onChange("first_name")} data-testid="client-first" /></div>
                        <div><label className={label}>Last Name *</label><input className={field} value={form.last_name} onChange={onChange("last_name")} data-testid="client-last" /></div>
                        <div><label className={label}>Email *</label><input type="email" className={field} value={form.email} onChange={onChange("email")} data-testid="client-email" /></div>
                        <div><label className={label}>Phone *</label><input className={field} value={form.phone} onChange={onChange("phone")} data-testid="client-phone" /></div>
                      </div>
                      <div className="mb-8"><label className={label}>Notes</label><textarea rows={2} className={field} value={form.notes} onChange={onChange("notes")} placeholder="Access details, concerns, gate codes…" data-testid="client-notes" /></div>

                      <label className="flex items-start gap-3 mb-10 cursor-pointer">
                        <span className={`mt-0.5 w-5 h-5 flex-shrink-0 border flex items-center justify-center ${form.sms_consent ? "bg-gold border-gold text-ink" : "border-white/40"}`} onClick={() => set("sms_consent", !form.sms_consent)}>
                          {form.sms_consent && <Check size={13} />}
                        </span>
                        <span className="text-cream/50 text-xs leading-relaxed" onClick={() => set("sms_consent", !form.sms_consent)}>
                          By entering my phone number I agree to receive SMS text messages from Home Point Inspections, Inc. Message frequency may vary; standard rates may apply. Reply STOP to unsubscribe.
                        </span>
                      </label>

                      <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-6">Agent <span className="text-cream/40 text-sm font-sans normal-case tracking-normal">(optional)</span></h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div><label className={label}>Agent Name</label><input className={field} value={form.agent_name} onChange={onChange("agent_name")} data-testid="agent-name" /></div>
                        <div><label className={label}>Agent Email</label><input type="email" className={field} value={form.agent_email} onChange={onChange("agent_email")} data-testid="agent-email" /></div>
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 4 — Review */}
                  {step === 3 && (
                    <motion.div key="s3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }}>
                      <h2 className="font-display uppercase text-cream text-2xl tracking-tight mb-8">Review & Schedule</h2>
                      <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        <ReviewRow label="Services" value={form.services.join(", ") || "—"} />
                        <ReviewRow label="Date & Time" value={`${form.preferred_date || "—"}${form.preferred_time ? " · " + form.preferred_time : ""}`} />
                        <ReviewRow label="Property" value={`${form.address}${form.unit ? " " + form.unit : ""}, ${form.city}, ${form.state} ${form.zip_code}`} />
                        <ReviewRow label="Under Contract" value={form.under_contract || "—"} />
                        <ReviewRow label="Client" value={`${form.first_name} ${form.last_name}`} />
                        <ReviewRow label="Contact" value={`${form.email} · ${form.phone}`} />
                        <ReviewRow label="Agent" value={form.agent_name || "—"} />
                        <ReviewRow label="Notes" value={form.notes || "—"} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Nav buttons */}
              <div className="flex items-center justify-between mt-8">
                <button onClick={back} disabled={step === 0}
                  className="inline-flex items-center gap-2 border border-white/20 text-cream px-6 py-3 font-display uppercase tracking-[0.12em] text-xs hover:border-gold hover:text-gold transition-colors disabled:opacity-30" data-testid="schedule-back">
                  <ChevronLeft size={16} /> Back
                </button>
                {step < 3 ? (
                  <button onClick={next} className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-3 font-display uppercase tracking-[0.12em] text-xs hover:bg-gold-hover transition-colors" data-testid="schedule-next">
                    Next <ChevronRight size={16} />
                  </button>
                ) : (
                  <button onClick={submit} disabled={loading} className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-3 font-display uppercase tracking-[0.12em] text-xs hover:bg-gold-hover transition-colors disabled:opacity-60" data-testid="schedule-submit">
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                    {loading ? "Submitting…" : "Schedule Inspection"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="bg-ink p-5">
      <div className="font-display uppercase tracking-[0.18em] text-[10px] text-gold/70 mb-1">{label}</div>
      <div className="text-cream text-sm break-words">{value}</div>
    </div>
  );
}
